/**
 * Pure progress-calculation helpers for the Contact Information page.
 *
 * Each helper receives a watched form slice and reports how many of its
 * required fields are filled. `computeProgressPercent` aggregates them into the
 * single 0-100 value rendered by the progress bar. Keeping these pure (no hooks,
 * no product branching beyond the flags passed in) makes them trivially testable
 * and keeps cognitive complexity out of the page component.
 */

export interface FieldProgress {
    filled: number;
    total: number;
}

export interface ProgressInputs {
    contactInfo: any;
    statementOfHealthData: any;
    coverageReplacementData: any;
    policyBeneficiaryData: any;
    secondaryAddresseeData: any;
    isDTO: boolean;
    isGIWL: boolean;
}

/** Counts filled contact-information fields (11 required). */
export const calculateContactInfoProgress = (data: any): FieldProgress => {
    if (!data) return { filled: 0, total: 0 };
    const fields = [
        data.firstName?.trim(),
        data.lastName?.trim(),
        data.dateOfBirth?.month?.trim(),
        data.dateOfBirth?.day?.trim(),
        data.dateOfBirth?.year?.trim(),
        data.email?.trim(),
        data.phone?.trim(),
        data.phoneType?.trim(),
        data.address?.trim(),
        data.city?.trim(),
        data.zipCode?.trim(),
    ];
    return { filled: fields.filter(Boolean).length, total: 11 };
};

/** Counts filled statement-of-health fields, treating booleans as set when not null/undefined. */
export const calculateHealthProgress = (data: any): FieldProgress => {
    if (!data) return { filled: 0, total: 0 };
    const fields = [
        data.height?.feet?.trim(),
        data.height?.inches?.trim(),
        data.weight?.trim(),
        data.hasMedicalConditions !== null && data.hasMedicalConditions !== undefined ? data.hasMedicalConditions : null,
        data.hasDiagnosticTesting !== null && data.hasDiagnosticTesting !== undefined ? data.hasDiagnosticTesting : null,
    ];
    return { filled: fields.filter((val) => val !== null && val !== undefined && val !== "").length, total: 5 };
};

/** Counts coverage-replacement fields; replacement detail fields only count when the user is replacing. */
export const calculateCoverageReplacementProgress = (data: any): FieldProgress => {
    if (!data) return { filled: 0, total: 0 };
    const { isReplacing } = data;
    let filled = 0;
    let total = 1; // Radio button is always required

    if (isReplacing !== null && isReplacing !== undefined) {
        filled += 1; // Radio button is set
        if (isReplacing === true) {
            const replacementFields = [data.replacementCompany?.trim(), data.replacementAccount?.trim()];
            filled += replacementFields.filter(Boolean).length;
            total += replacementFields.length;
        }
    }
    return { filled, total };
};

/** Counts filled policy-beneficiary fields (3 required). */
export const calculateBeneficiaryProgress = (data: any): FieldProgress => {
    if (!data) return { filled: 0, total: 0 };
    const fields = [data.firstName?.trim(), data.lastName?.trim(), data.relationship?.trim()];
    return { filled: fields.filter(Boolean).length, total: 3 };
};

/** Counts secondary-addressee fields; detail fields only count when a secondary addressee is present. */
export const calculateSecondaryAddresseeProgress = (data: any): FieldProgress => {
    if (!data) return { filled: 0, total: 0 };
    const { hasSecondaryAddressee } = data;
    let filled = 0;
    let total = 1; // Radio button is always required

    if (hasSecondaryAddressee !== null && hasSecondaryAddressee !== undefined) {
        filled += 1; // Radio button is set
        if (hasSecondaryAddressee === true) {
            const addresseeFields = [
                data.firstName?.trim(),
                data.lastName?.trim(),
                data.address?.trim(),
                data.city?.trim(),
                data.zipCode?.trim(),
                data.phone?.trim(),
            ];
            filled += addresseeFields.filter(Boolean).length;
            total += addresseeFields.length;
        }
    }
    return { filled, total };
};

/**
 * Aggregates per-section progress into a single 0-100 percentage.
 *
 * Health is only included for DTO; coverage replacement is included for DTO and
 * GIWL. Returns 0 until the always-present sections
 * (contact, beneficiary, secondary addressee) have data.
 */
export const computeProgressPercent = ({
    contactInfo,
    statementOfHealthData,
    coverageReplacementData,
    policyBeneficiaryData,
    secondaryAddresseeData,
    isDTO,
    isGIWL,
}: ProgressInputs): number => {
    if (!contactInfo || !policyBeneficiaryData || !secondaryAddresseeData) return 0;

    const contactProgress = calculateContactInfoProgress(contactInfo);
    const beneficiaryProgress = calculateBeneficiaryProgress(policyBeneficiaryData);
    const addresseeProgress = calculateSecondaryAddresseeProgress(secondaryAddresseeData);

    let totalFilled = contactProgress.filled + beneficiaryProgress.filled + addresseeProgress.filled;
    let totalFields = contactProgress.total + beneficiaryProgress.total + addresseeProgress.total;

    if (isDTO) {
        const healthProgress = calculateHealthProgress(statementOfHealthData);
        totalFilled += healthProgress.filled;
        totalFields += healthProgress.total;
    }

    if (isDTO || isGIWL) {
        const coverageProgress = calculateCoverageReplacementProgress(coverageReplacementData);
        totalFilled += coverageProgress.filled;
        totalFields += coverageProgress.total;
    }

    return totalFields > 0 ? Math.round((totalFilled / totalFields) * 100) : 0;
};
